/**
 * 全局 SSE 单例客户端。
 *
 * 收敛为单一 EventSource 连接：Layout、通知与任务管理共用同一条连接，
 * 避免多组件各自建立重复长连接。客户端只负责连接与事件分发，
 * 业务逻辑（写 taskStore、更新本地任务、展示通知）由订阅者处理。
 */
import { API_ENDPOINTS, SOCKET_URL } from './apiConfig';
import { apiClient } from './apiClient';

export type SSEEventHandler = (data: Record<string, unknown>) => void;

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const SUPPORTED_EVENTS = ['task_lifecycle', 'task_stream', 'notification', 'system', 'heartbeat'];

interface SseTicketResponse {
  ticket: string;
  expiresIn: number;
}

class SSEClient {
  private eventSource: EventSource | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;
  private token: string | null = null;
  private onConnected?: () => void;
  private reconnectAttempt = 0;
  private generation = 0;
  private handlers = new Map<string, Set<SSEEventHandler>>();

  connect(token: string, onConnected?: () => void): void {
    if (this.shouldReconnect) return;
    this.shouldReconnect = true;
    this.token = token;
    this.onConnected = onConnected;
    this.reconnectAttempt = 0;
    this.generation += 1;
    void this.open(this.generation);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.token = null;
    this.onConnected = undefined;
    this.reconnectAttempt = 0;
    this.generation += 1;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.eventSource?.close();
    this.eventSource = null;
  }

  subscribe(eventType: string, handler: SSEEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  private async open(generation: number): Promise<void> {
    if (!this.shouldReconnect || generation !== this.generation || !this.token) return;

    try {
      const { data } = await apiClient.post<SseTicketResponse>(
        API_ENDPOINTS.ai.sseTicket,
        undefined,
        this.token
      );
      if (!this.shouldReconnect || generation !== this.generation) return;

      const eventSource = new EventSource(
        `${SOCKET_URL}/api/ai/sse?ticket=${encodeURIComponent(data.ticket)}`
      );
      this.eventSource = eventSource;
      eventSource.onopen = () => {
        if (generation !== this.generation) return;
        this.reconnectAttempt = 0;
        this.onConnected?.();
      };

      for (const eventType of SUPPORTED_EVENTS) {
        eventSource.addEventListener(eventType, (event) => {
          try {
            const eventData = JSON.parse((event as MessageEvent).data);
            this.emit(eventType, eventData);
          } catch {
            // SSE 事件为尽力而为的 UI 更新，解析失败忽略。
          }
        });
      }

      eventSource.onerror = () => {
        eventSource.close();
        if (this.eventSource === eventSource) this.eventSource = null;
        this.scheduleReconnect(generation);
      };
    } catch {
      this.scheduleReconnect(generation);
    }
  }

  private scheduleReconnect(generation: number): void {
    if (!this.shouldReconnect || generation !== this.generation || this.reconnectTimer) return;
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * (2 ** this.reconnectAttempt),
      RECONNECT_MAX_DELAY_MS
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.open(generation);
    }, delay);
  }

  private emit(eventType: string, data: Record<string, unknown>): void {
    this.handlers.get(eventType)?.forEach((handler) => {
      try {
        handler(data);
      } catch {
        // 订阅者异常不应影响其它订阅者。
      }
    });
  }
}

export const sseClient = new SSEClient();
